import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import {
  ArrowLeft, ArrowUpRight, CalendarCheck, QrCode, Languages, WheatOff, Smartphone, BadgePercent, BellRing, CalendarRange,
} from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { SectionTag, EASE } from "@/components/Reveal";
import BookingDemo from "@/components/demo/BookingDemo";
import MenuDemo from "@/components/demo/MenuDemo";
import { OwnerFeed } from "@/components/demo/OwnerFeed";
import Footer from "@/components/Footer";

const TABS = [
  { id: "prenotazioni", label: "Prenotazioni Direct", icon: CalendarCheck },
  { id: "menu", label: "Menù digitale QR", icon: QrCode },
];

const BOOKING_POINTS = [
  { icon: BadgePercent, title: "Zero commissioni", text: "Nessuna percentuale a TheFork o Booking: il tavolo lo prenota da te." },
  { icon: BellRing, title: "Notifica immediata", text: "Il locale riceve push + email; il cliente un SMS con codice." },
  { icon: CalendarRange, title: "Turni e chiusure", text: "Pranzo/cena, giorni di chiusura e posti rimasti in tempo reale." },
];

const MENU_POINTS = [
  { icon: Languages, title: "3 lingue incluse", text: "IT · EN · DE con un tocco. Altre lingue su richiesta." },
  { icon: WheatOff, title: "Allergeni a norma UE", text: "Filtri «senza glutine», «senza crostacei», «senza latticini»." },
  { icon: Smartphone, title: "Aggiorni dal telefono", text: "Cambi prezzo o piatto del giorno in 30 secondi, zero ristampe." },
];

function Points({ items }) {
  return (
    <ul className="space-y-4">
      {items.map(({ icon: Icon, title, text }) => (
        <li key={title} className="flex gap-4">
          <span className="flex h-10 w-10 flex-none items-center justify-center rounded-xl border border-line bg-ink text-terracotta">
            <Icon size={17} strokeWidth={1.8} />
          </span>
          <div>
            <p className="text-sm font-medium text-bone">{title}</p>
            <p className="mt-0.5 text-sm leading-relaxed text-stone">{text}</p>
          </div>
        </li>
      ))}
    </ul>
  );
}

export default function Demo() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "menu" ? "menu" : "prenotazioni";
  const [bookings, setBookings] = useState([]);
  const menuUrl = `${window.location.origin}/demo?tab=menu`;

  useEffect(() => {
    document.title = "Demo interattive · Nex Studio Design";
  }, []);

  return (
    <main className="min-h-screen overflow-x-hidden bg-ink">
      <header className="glass sticky top-0 z-40 border-b border-line">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
          <Link to="/" className="flex items-center gap-3" data-testid="demo-logo-link">
            <LogoMark size={28} />
            <span className="font-serif text-base tracking-tight text-bone">Nex Studio Design</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="demo-back-link"
              className="hidden items-center gap-2 rounded-full border border-line px-4 py-2 font-accent text-[10px] uppercase tracking-[0.2em] text-stone transition-colors duration-300 hover:border-terracotta hover:text-terracotta sm:inline-flex"
            >
              <ArrowLeft size={13} /> Torna al sito
            </Link>
            <Link
              to="/#contatti"
              data-testid="demo-cta-button"
              className="group inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-2.5 font-accent text-[11px] font-semibold uppercase tracking-[0.18em] text-[#1c0d08] transition-colors duration-300 hover:bg-[#f0695a]"
            >
              Lo voglio sul mio sito
              <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </header>

      <section className="mx-auto max-w-7xl px-5 pb-10 pt-16 sm:px-8 lg:pt-24">
        <SectionTag>Demo interattive</SectionTag>
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mt-5 max-w-3xl font-serif text-4xl font-medium leading-[1.05] tracking-tight text-bone sm:text-5xl lg:text-6xl"
        >
          Provalo come lo proverebbe <em className="italic text-terracotta">il tuo cliente.</em>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
          className="mt-5 max-w-2xl text-base leading-relaxed text-stone sm:text-lg"
        >
          Due strumenti che installiamo nei siti dei nostri clienti, qui funzionanti con i piatti reali del Ristorante Casa
          Fortuna di Mestre. Nessun dato viene salvato: gioca pure.
        </motion.p>

        <div className="mt-10 inline-flex rounded-full border border-line bg-panel p-1" role="tablist">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              role="tab"
              aria-selected={tab === id}
              onClick={() => setParams(id === "menu" ? { tab: "menu" } : {})}
              data-testid={`demo-tab-${id}`}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-accent text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors duration-300 ${
                tab === id ? "bg-terracotta text-[#1c0d08]" : "text-stone hover:text-bone"
              }`}
            >
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8" id="prenota">
        {tab === "prenotazioni" ? (
          <motion.div
            key="prenotazioni"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="grid gap-6 lg:grid-cols-12"
          >
            <div className="lg:col-span-7">
              <BookingDemo onConfirm={(b) => setBookings((bs) => [b, ...bs])} />
            </div>
            <div className="space-y-6 lg:col-span-5">
              <OwnerFeed bookings={bookings} />
              <div className="rounded-3xl border border-line bg-panel p-6">
                <Points items={BOOKING_POINTS} />
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="menu"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="grid items-start gap-10 lg:grid-cols-12"
          >
            <div className="lg:col-span-5">
              <MenuDemo />
            </div>
            <div className="space-y-6 lg:col-span-7">
              <div className="flex flex-col gap-6 rounded-3xl border border-line bg-panel p-6 sm:flex-row sm:items-center sm:p-8">
                <div className="flex-none rounded-2xl bg-bone p-3" data-testid="menu-qr-code">
                  <QRCodeSVG value={menuUrl} size={132} bgColor="#F5F2EB" fgColor="#0E0C0B" level="M" />
                </div>
                <div>
                  <p className="font-accent text-[10px] uppercase tracking-[0.3em] text-faded">Prova dal vero</p>
                  <h3 className="mt-2 font-serif text-2xl tracking-tight text-bone">Inquadra il QR con il telefono</h3>
                  <p className="mt-2 text-sm leading-relaxed text-stone">
                    È lo stesso codice che stamperemmo sui tavoli: si apre il menù, senza app e senza download. Cambia lingua,
                    filtra per allergeni, chiama o prenota.
                  </p>
                </div>
              </div>
              <div className="rounded-3xl border border-line bg-panel p-6 sm:p-8">
                <Points items={MENU_POINTS} />
              </div>
              <p className="px-1 text-xs leading-relaxed text-faded">
                Piatti e dolci tratti dal menù pubblicato da Casa Fortuna; descrizioni, traduzioni e prezzi sono indicativi e
                servono solo a mostrare il prodotto.
              </p>
            </div>
          </motion.div>
        )}
      </section>

      <section className="border-t border-line bg-panel">
        <div className="mx-auto flex max-w-7xl flex-col gap-6 px-5 py-16 sm:px-8 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="font-accent text-[10px] uppercase tracking-[0.3em] text-terracotta">Incluso nei piani Pro e Tailored</p>
            <h2 className="mt-3 font-serif text-3xl tracking-tight text-bone sm:text-4xl">
              Lo vuoi sul tuo sito, con i tuoi piatti e i tuoi orari?
            </h2>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/#piani"
              data-testid="demo-plans-link"
              className="rounded-full border border-line-strong px-6 py-3.5 font-accent text-xs uppercase tracking-[0.18em] text-bone transition-colors duration-300 hover:border-bone/50"
            >
              Vedi i piani
            </Link>
            <Link
              to="/#contatti"
              data-testid="demo-contact-link"
              className="rounded-full bg-terracotta px-6 py-3.5 font-accent text-xs font-semibold uppercase tracking-[0.18em] text-[#1c0d08] transition-colors duration-300 hover:bg-[#f0695a]"
            >
              Richiedi audit gratuito
            </Link>
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
}
